import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { IndianRupee, Layers, Plug, Rocket, ArrowUpRight, ArrowDownRight } from "lucide-react";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

function Stat({ label, value, sub, icon: Icon, tone }) {
  return (
    <div className="p-6 bg-white" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">{label}</span>
        <Icon className="w-4 h-4 text-slate-400" />
      </div>
      <div className={`font-mono text-3xl font-medium tracking-tight ${tone || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/trading/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const spark = (data?.recent_orders || []).slice().reverse().reduce((acc, o, i) => {
    const prev = i ? acc[i - 1].v : 0;
    acc.push({ i, v: prev + (o.pnl || 0) });
    return acc;
  }, []);

  return (
    <Layout>
      <PageHeader title="Dashboard" subtitle="Portfolio overview & live activity" />
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-slate-200 mb-8">
          <div className="border-r border-b border-slate-200">
            <Stat label="Total P&L" value={`₹${fmt(data?.total_pnl)}`} icon={IndianRupee}
              tone={(data?.total_pnl || 0) >= 0 ? "text-emerald-600" : "text-rose-600"} sub="Across all orders" />
          </div>
          <div className="border-r border-b border-slate-200">
            <Stat label="Strategies" value={fmt(data?.strategies)} icon={Layers} sub="Total created" />
          </div>
          <div className="border-r border-b border-slate-200">
            <Stat label="Connected Brokers" value={fmt(data?.connected_brokers)} icon={Plug} sub="Active connections" />
          </div>
          <div className="border-r border-b border-slate-200">
            <Stat label="Running" value={`${fmt(data?.running_live)}L / ${fmt(data?.running_paper)}P`} icon={Rocket} sub="Live / Paper deployments" />
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 border border-slate-200 bg-white">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-bold tracking-tight">Cumulative P&L</h3>
              <span className="text-xs font-mono text-slate-400">Recent orders</span>
            </div>
            <div className="p-4 h-72">
              {spark.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={spark}>
                    <defs>
                      <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="i" hide />
                    <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} width={60} />
                    <Tooltip formatter={(v) => `₹${fmt(v)}`} />
                    <Area type="monotone" dataKey="v" stroke="#10B981" strokeWidth={2} fill="url(#g)" animationDuration={800} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                  <pre className="font-mono text-xs mb-2">┌─────┐{"\n"}│ ─── │{"\n"}└─────┘</pre>
                  <p className="text-sm">No orders yet. Deploy a strategy to see activity.</p>
                </div>
              )}
            </div>
          </div>

          <div className="border border-slate-200 bg-white">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-heading font-bold tracking-tight">Active Deployments</h3>
            </div>
            <div className="divide-y divide-slate-100">
              {(data?.active_deployments || []).length === 0 && (
                <div className="p-6 text-sm text-slate-400">Nothing running.</div>
              )}
              {(data?.active_deployments || []).map((d) => (
                <div key={d.id} className="px-6 py-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer"
                  onClick={() => nav("/autotrade")}>
                  <div>
                    <div className="text-sm font-semibold">{d.strategy_name}</div>
                    <div className="text-xs text-slate-500 font-mono">{d.underlying} · {d.mode.toUpperCase()}</div>
                  </div>
                  <span className={`font-mono text-sm ${(d.pnl || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                    {(d.pnl || 0) >= 0 ? "+" : ""}₹{fmt(d.pnl)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 border border-slate-200 bg-white">
          <div className="px-6 py-4 border-b border-slate-200">
            <h3 className="font-heading font-bold tracking-tight">Recent Orders</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold tracking-wide uppercase text-slate-500 border-b border-slate-200">
                <th className="px-6 py-3">Symbol</th><th className="px-6 py-3">Side</th>
                <th className="px-6 py-3 text-right">Qty</th><th className="px-6 py-3 text-right">Price</th>
                <th className="px-6 py-3 text-right">P&L</th><th className="px-6 py-3">Mode</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_orders || []).length === 0 && (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-slate-400">No orders yet.</td></tr>
              )}
              {(data?.recent_orders || []).map((o) => (
                <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-6 py-2.5 font-mono">{o.symbol}</td>
                  <td className="px-6 py-2.5">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${o.side === "BUY" ? "text-emerald-600" : "text-rose-600"}`}>
                      {o.side === "BUY" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}{o.side}
                    </span>
                  </td>
                  <td className="px-6 py-2.5 text-right font-mono">{o.qty}</td>
                  <td className="px-6 py-2.5 text-right font-mono">₹{fmt(o.price)}</td>
                  <td className={`px-6 py-2.5 text-right font-mono ${(o.pnl || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{(o.pnl || 0) >= 0 ? "+" : ""}₹{fmt(o.pnl)}</td>
                  <td className="px-6 py-2.5 text-xs uppercase text-slate-500">{o.mode}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
